import { DOCUMENT, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, map, Observable, shareReplay, startWith } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PartnerKey, mapPartnerIdToKey } from '../models/partner-key';
import { SessionService } from './session.service';

export interface BrandAssets {
	id: PartnerKey;
	displayName: string;
	themeKey?: string;
	faviconUrl: string;
	logoUrl: string;
	logoNoTextUrl: string;
}

const BRAND_DEFINITIONS: Record<PartnerKey, BrandAssets> = {
	[PartnerKey.Default]: {
		id: PartnerKey.Default,
		displayName: 'Trovata',
		themeKey: undefined,
		faviconUrl: 'assets/favicon.ico',
		logoUrl: 'assets/logo-trovata.svg',
		logoNoTextUrl: 'assets/logoNoText-sm-trovata.png',
	},
	[PartnerKey.WellsFargo]: {
		id: PartnerKey.WellsFargo,
		displayName: 'Wells Fargo',
		themeKey: PartnerKey.WellsFargo,
		faviconUrl: 'assets/wells-fargo-favicon.ico',
		logoUrl: 'assets/logo-wells-fargo.svg',
		logoNoTextUrl: 'assets/logoNoText-sm-wells-fargo.png',
	},
};

@Injectable({
	providedIn: 'root',
})
export class BrandService {
	private document: Document = inject(DOCUMENT);

	private sessionService: SessionService = inject(SessionService);

	brandAssets$: Observable<BrandAssets> = this.sessionService.partnerId$.pipe(
		map((partnerId: string | null) => mapPartnerIdToKey(partnerId)),
		startWith(this.document.location.hostname === environment.wfWidgetHost ? PartnerKey.WellsFargo : PartnerKey.Default),
		distinctUntilChanged(),
		map((partnerKey: PartnerKey) => BRAND_DEFINITIONS[partnerKey]),
		shareReplay(1)
	);

	constructor() {
		const root: HTMLElement = this.document.documentElement;
		this.brandAssets$.pipe(takeUntilDestroyed()).subscribe((brand: BrandAssets) => {
			root.setAttribute('data-partner', brand.id);

			if (brand.themeKey) {
				root.setAttribute('data-partner-theme', brand.themeKey);
			} else {
				root.removeAttribute('data-partner-theme');
			}

			this.getFaviconLinkElement().href = brand.faviconUrl;
		});
	}

	private getFaviconLinkElement(): HTMLLinkElement {
		const existingFaviconElement: HTMLLinkElement | null = this.document.head.querySelector("link[rel~='icon']");
		if (existingFaviconElement) {
			return existingFaviconElement;
		}

		const newFaviconElement: HTMLLinkElement = this.document.createElement('link');
		newFaviconElement.rel = 'icon';
		this.document.head.appendChild(newFaviconElement);
		return newFaviconElement;
	}
}
